import { styled } from '@linaria/react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRoadmap } from '../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { setActivePanel } from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { StrategyHeader, StrategyTabsList } from '../strategy-header';
import { StrategyTab } from '../strategy.type';
import { GoalSection } from './components/goal-section';
import { RoadmapHeader } from './components/roadmap-header';
import { RoadmapFilters } from './roadmap-filters';
import { calculateTimelineBounds } from './roadmap.helpers';
import type { DeliveryProgressFilter, YearFilter } from './roadmap.type';
import { LEFT_PADDING } from './roadmap.type';

export function StrategyRoadmap() {
  useDocumentTitle('Roadmap - Bloomfilter');

  const i18n = useTranslation();
  const portfolio = useGlobalStore((state) => state.portfolio);
  const activePanel = useStrategyStore((state) => state.activePanel);
  const { roadmap, query } = useRoadmap(portfolio?.id);
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [expandedOutcomes, setExpandedOutcomes] = useState<Set<string>>(new Set());
  const [deliveryProgressFilter, setDeliveryProgressFilter] = useState<DeliveryProgressFilter>({
    notStarted: true,
    inProgress: true,
    nearComplete: true,
    complete: true,
  });
  const [yearFilter, setYearFilter] = useState<YearFilter>({
    selectedYear: dayjs().year(), // default to current year
  });
  const [showTodayLine, setShowTodayLine] = useState(true);

  const goals = roadmap?.goals || [];
  const today = dayjs();

  const handleSetFilterShow = (show: boolean) => {
    setActivePanel(show ? Panel.Filters : null);
  };

  // Auto-expand all goals on initial mount
  // IMPORTANT: This must be called before any conditional returns to follow Rules of Hooks
  const initializedRef = useRef(false);

  useEffect(() => {
    if (goals.length > 0 && !initializedRef.current) {
      // Expand all goals (including No Goal)
      const allGoalIds = goals.map((g) => g.id);
      setExpandedGoals(new Set(allGoalIds));
      initializedRef.current = true;
    }
  }, [goals]);

  // Calculate timeline bounds - start from earliest initiative start date
  // IMPORTANT: This must be called before any conditional returns to follow Rules of Hooks
  const { timelineStart, timelineEnd, totalDays } = useMemo(() => {
    return calculateTimelineBounds(goals, yearFilter.selectedYear);
  }, [goals, yearFilter.selectedYear]);

  // Calculate number of months for flexible column sizing
  const timelineWidth = '100%';

  // Show error state
  if (query.isError) {
    return (
      <MenuDrivenContainer header={<StrategyHeader />}>
        <StrategyTabsList active={StrategyTab.Roadmap} />
        <Container>
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--red-base)' }}>
            Error loading roadmap data. Please try again.
          </div>
        </Container>
      </MenuDrivenContainer>
    );
  }

  const toggleGoal = (goalId: string) => {
    setExpandedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(goalId)) {
        next.delete(goalId);
      } else {
        next.add(goalId);
      }
      return next;
    });
  };

  const toggleOutcome = (outcomeId: string) => {
    setExpandedOutcomes((prev) => {
      const next = new Set(prev);
      if (next.has(outcomeId)) {
        next.delete(outcomeId);
      } else {
        next.add(outcomeId);
      }
      return next;
    });
  };

  // Determine if we should show the today line based on the selected year
  const shouldShowTodayLine =
    showTodayLine && (yearFilter.selectedYear === null || today.year() === yearFilter.selectedYear);

  const roadmapContent = (
    <Container>
      <RoadmapHeader
        timelineStart={timelineStart}
        timelineEnd={timelineEnd}
        today={today}
        timelineWidth={timelineWidth}
        totalDays={totalDays}
        selectedYear={yearFilter.selectedYear}
        showTodayLine={showTodayLine}
      />
      <Content>
        {shouldShowTodayLine && (
          <TodayLineOverlay
            style={{
              left: `calc(${LEFT_PADDING + 64}px + (100% - ${LEFT_PADDING + 64}px) * ${(
                today.diff(timelineStart, 'day') / totalDays
              ).toFixed(4)})`,
            }}
          />
        )}
        <ScrollContainer>
          {goals.map((goal) => (
            <GoalSection
              key={goal.id}
              goal={goal}
              isExpanded={expandedGoals.has(goal.id)}
              onToggle={() => toggleGoal(goal.id)}
              expandedOutcomes={expandedOutcomes}
              onToggleOutcome={toggleOutcome}
              timelineStart={timelineStart}
              timelineEnd={timelineEnd}
              timelineWidth={timelineWidth}
              totalDays={totalDays}
              deliveryProgressFilter={deliveryProgressFilter}
              selectedYear={yearFilter.selectedYear}
            />
          ))}
        </ScrollContainer>
      </Content>
    </Container>
  );

  return (
    <MenuDrivenContainer header={<StrategyHeader />}>
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" transitionDuration={30} />
      <PageLayout
        controlBar={<StrategyTabsList active={StrategyTab.Roadmap} />}
        sideFilters={{
          title: i18n.t('common.filters'),
          content: (
            <RoadmapFilters
              setShow={handleSetFilterShow}
              title={i18n.t('common.filters')}
              deliveryProgressFilter={deliveryProgressFilter}
              onDeliveryProgressFilterChange={setDeliveryProgressFilter}
              goals={goals}
              yearFilter={yearFilter}
              onYearFilterChange={setYearFilter}
              showTodayLine={showTodayLine}
              onShowTodayLineChange={setShowTodayLine}
            />
          ),
          show: activePanel === Panel.Filters,
          setShow: handleSetFilterShow,
        }}
        mainSection={roadmapContent}
        showSideSection={false}
      />
    </MenuDrivenContainer>
  );
}

// Styled Components
const Container = styled.div`
  border: 1px solid var(--sky-light);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--sky-lightest);
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;

  /* Hide scrollbars for Chrome, Safari, and Edge */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbars for Firefox */
  scrollbar-width: none;

  /* Hide scrollbars for IE and Edge */
  -ms-overflow-style: none;
`;

const TodayLineOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: var(--red-base);
  z-index: 5;
  pointer-events: none;
`;
